import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, MessageCircle, Syringe, Beef, Utensils, Dumbbell } from "lucide-react";
import { Toaster, toast } from "sonner";
import { CLINIC, LOGO_SVG, doctorDisplay, buildWhatsAppBrandedMessage } from "../lib/clinic";

/**
 * GLP-1 Companion Kit — full patient hand-out that ships with every GLP-1 Rx.
 * Combines four sections onto one branded, printable, WhatsApp-shareable page:
 *   (1) Do's & Don'ts     (2) Protein intake + shake guide
 *   (3) Diet Hub deep-link (Maharashtrian low-carb)
 *   (4) Exercise Hub deep-link (Muscle strengthening)
 */
export default function GLP1CompanionKit() {
  const { kid } = useParams();
  const nav = useNavigate();
  const [kit, setKit] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/glp1/kits/${kid}`);
        setKit(r.data.kit);
      } catch (e) {
        console.warn("kit load failed:", e);
        toast.error("Could not load GLP-1 companion kit");
      }
    })();
  }, [kid]);

  if (!kit) return <div className="p-8 text-sm text-slate-500">Loading kit…</div>;
  const isMr = kit.language === "mr";
  const date = new Date(kit.issued_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const detected = kit.glp1_medicines_detected || [];

  // Render a Do's & Don'ts section
  const renderDD = () => {
    const dd = kit.dos_donts;
    return (
      <section className="mb-8 print-avoid-break" data-testid="glp1-dd">
        <div className="flex items-center gap-2 mb-3 pb-1 border-b-2 border-pink-600">
          <Syringe size={18} className="text-pink-600" />
          <h2 className="font-heading text-lg font-bold text-slate-900">{dd.title}</h2>
        </div>
        <p className="text-[11px] text-slate-600 italic leading-relaxed mb-3">{dd.intro}</p>
        {dd.sections.map((sec, i) => (
          <div key={i} className="mb-4">
            <h3 className="font-semibold text-[12px] text-slate-800 mb-1.5">{sec.title}</h3>
            {sec.dos?.length > 0 && (
              <div className="mb-2">
                <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">
                  {isMr ? "करा" : "DO"}
                </div>
                <ul className="text-[10.5px] text-slate-700 pl-4 leading-snug list-disc space-y-0.5">
                  {sec.dos.map((it, j) => <li key={j}>{it}</li>)}
                </ul>
              </div>
            )}
            {sec.donts?.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-rose-700 font-semibold mb-0.5">
                  {isMr ? "करू नका" : "DON'T"}
                </div>
                <ul className="text-[10.5px] text-slate-700 pl-4 leading-snug list-disc space-y-0.5">
                  {sec.donts.map((it, j) => <li key={j}>{it}</li>)}
                </ul>
              </div>
            )}
          </div>
        ))}
        <p className="text-[10.5px] text-slate-700 italic leading-relaxed mt-3 p-2 rounded bg-pink-50 border border-pink-200">
          {dd.closing}
        </p>
      </section>
    );
  };

  // Render Protein Guide
  const renderProtein = () => {
    const pg = kit.protein_guide;
    return (
      <section className="mb-8 print-avoid-break" data-testid="glp1-protein">
        <div className="flex items-center gap-2 mb-3 pb-1 border-b-2 border-amber-600">
          <Beef size={18} className="text-amber-600" />
          <h2 className="font-heading text-lg font-bold text-slate-900">{pg.title}</h2>
        </div>
        <p className="text-[11px] text-slate-600 italic leading-relaxed mb-3">{pg.intro}</p>
        {pg.sections.map((sec, i) => (
          <div key={i} className="mb-4">
            <h3 className="font-semibold text-[12px] text-slate-800 mb-1.5">{sec.title}</h3>
            {sec.body && (
              <pre className="text-[10.5px] text-slate-700 leading-relaxed whitespace-pre-wrap font-sans mb-2">
                {sec.body}
              </pre>
            )}
            {sec.table && (
              <table className="w-full text-[10px] border-collapse mb-1">
                <tbody>
                  {sec.table.map((row, ri) => (
                    <tr key={ri} className="border-b border-slate-100">
                      {row.map((cell, ci) => (
                        <td key={ci} className={`px-1.5 py-1 align-top ${ci === 0 ? "font-medium text-slate-800 w-1/3" : "text-slate-600"}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
        <p className="text-[10.5px] text-slate-700 italic leading-relaxed mt-3 p-2 rounded bg-amber-50 border border-amber-200">
          {pg.closing}
        </p>
      </section>
    );
  };

  // Deep-link cards for Diet & Exercise Hub defaults
  const renderHubs = () => (
    <section className="grid grid-cols-2 gap-3 mb-6" data-testid="glp1-hub-cards">
      <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/60">
        <div className="flex items-center gap-2 mb-1.5">
          <Utensils size={16} className="text-emerald-700" />
          <h3 className="font-semibold text-[12px] text-slate-800">
            {isMr ? "आहार आराखडा (मराठी)" : "Personalised Diet Plan"}
          </h3>
        </div>
        <div className="text-[10.5px] text-slate-700 leading-snug">
          {isMr
            ? kit.recommended_diet_hub_defaults.diet_label_mr
            : kit.recommended_diet_hub_defaults.diet_label_en}
        </div>
        <div className="text-[9.5px] text-slate-500 mt-1">
          Macros — Protein {kit.recommended_diet_hub_defaults.macros.protein}% · Carb {kit.recommended_diet_hub_defaults.macros.carb}% · Fat {kit.recommended_diet_hub_defaults.macros.fat}%
        </div>
        <div className="text-[9px] text-slate-500 mt-1.5 italic">
          {isMr ? "→ डायट हब मध्ये तयार करा" : "→ Generate in Diet Hub"}
        </div>
      </div>

      <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/60">
        <div className="flex items-center gap-2 mb-1.5">
          <Dumbbell size={16} className="text-indigo-700" />
          <h3 className="font-semibold text-[12px] text-slate-800">
            {isMr ? "स्नायू बळकटीकरण व्यायाम" : "Muscle Strengthening Plan"}
          </h3>
        </div>
        <div className="text-[10.5px] text-slate-700 leading-snug">
          {isMr
            ? kit.recommended_exercise_hub_defaults.level_label_mr
            : kit.recommended_exercise_hub_defaults.level_label_en}
        </div>
        <div className="text-[9.5px] text-slate-500 mt-1">
          {isMr
            ? "प्रोग्रेसिव्ह ओव्हरलोड — बॉडी-वेट → १ लि. बाटल्या → ३-५ किलो → रेजिस्टन्स बँड"
            : "Progressive overload — body-weight → 1 L bottles → 3-5 kg → resistance band"}
        </div>
        <div className="text-[9px] text-slate-500 mt-1.5 italic">
          {isMr ? "→ एक्सरसाइज हब मध्ये तयार करा" : "→ Generate in Exercise Hub"}
        </div>
      </div>
    </section>
  );

  // Bundled WhatsApp share — sends the full kit as one long message
  const shareWA = () => {
    const dd = kit.dos_donts;
    const pg = kit.protein_guide;
    const lines = [`*${dd.title}*`, dd.intro, ""];
    dd.sections.forEach((sec) => {
      lines.push(`*${sec.title}*`);
      if (sec.dos?.length) {
        lines.push(isMr ? "_करा:_" : "_DO:_");
        sec.dos.forEach((it) => lines.push(`✅ ${it}`));
      }
      if (sec.donts?.length) {
        lines.push(isMr ? "_करू नका:_" : "_DON'T:_");
        sec.donts.forEach((it) => lines.push(`❌ ${it}`));
      }
      lines.push("");
    });
    lines.push(`_${dd.closing}_`, "");
    lines.push(`*${pg.title}*`, pg.intro, "");
    pg.sections.forEach((sec) => {
      lines.push(`*${sec.title}*`);
      if (sec.body) lines.push(sec.body);
      if (sec.table) sec.table.forEach((row) => lines.push(`• ${row.join(" — ")}`));
      lines.push("");
    });
    lines.push(`_${pg.closing}_`);
    const body = lines.join("\n");
    const msg = buildWhatsAppBrandedMessage({
      lang: isMr ? "mr" : "en",
      title: isMr ? "GLP-1 कंपॅनियन किट" : "GLP-1 Companion Kit",
      body,
      kind: "education",
      dateStr: date,
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <div className="print-toolbar sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 no-print">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="kit-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>
        <div className="text-sm text-slate-600 flex items-center gap-1">
          <Syringe size={14} /> GLP-1 Companion Kit · {kit.kit_id}
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={shareWA} className="border-emerald-300 text-emerald-700" data-testid="kit-wa">
            <MessageCircle size={14} className="mr-1" /> WhatsApp
          </Button>
          <Button size="sm" onClick={() => window.print()} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="kit-print">
            <Printer size={14} className="mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="max-w-[820px] mx-auto my-6 bg-white border border-slate-200 shadow-sm print-page" data-testid="kit-page">
        <div className="p-8">
          <header className="flex justify-between items-start pb-4 border-b-2 border-slate-900">
            <div className="flex gap-4 items-start">
              <div className="w-16 h-20 flex-shrink-0">{LOGO_SVG}</div>
              <div>
                <div className="font-heading text-xl font-bold text-slate-900 leading-tight">{CLINIC.name}</div>
                <div className="text-xs text-slate-700 mt-1 font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[10px] text-slate-500 leading-snug">{CLINIC.qualifications}</div>
                <div className="text-[10px] text-slate-500">{CLINIC.regNo}</div>
              </div>
            </div>
            <div className="text-right text-[10px] text-slate-600 max-w-[220px] leading-snug">
              <div>{CLINIC.address}</div>
              {(CLINIC.phones || []).length > 0 && <div className="mt-1 tabular">{CLINIC.phones.join(" · ")}</div>}
            </div>
          </header>

          <section className="mt-4 py-3 border-b border-slate-200 text-center">
            <div className="uppercase text-[10px] tracking-[0.25em] text-pink-600 font-semibold">
              {isMr ? "जीएलपी-१ कंपॅनियन किट" : "GLP-1 Companion Kit"}
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">
              {isMr ? "डायबेसिटी व्यवस्थापनाचे संपूर्ण मार्गदर्शक" : "Diabesity Management — Complete Guide"}
            </div>
            <div className="text-[10px] text-slate-500 mt-1 tabular">
              {isMr ? "दिनांक" : "Date"}: {date} · {isMr ? "किट क्र." : "Kit No."} {kit.kit_id}
              {detected.length > 0 && (
                <span> · {isMr ? "औषध" : "Injection"}: {detected.map((d) => d.brand_name || d.generic_name).join(", ")}</span>
              )}
            </div>
          </section>

          <div className="mt-6">
            {renderHubs()}
            {renderDD()}
            {renderProtein()}
          </div>

          <footer className="mt-6 pt-3 border-t border-slate-200 text-[9px] text-slate-500 leading-relaxed print-doctor-footer">
            {isMr
              ? <>ही माहिती <strong>{doctorDisplay(CLINIC.doctorName)}, ({CLINIC.qualifications})</strong> यांच्या क्लिनिकल पर्यवेक्षणाखाली <strong>{CLINIC.name}</strong> कडून प्रकाशित केली आहे. वैयक्तिक सल्ल्यासाठी कृपया क्लिनिकशी संपर्क साधा.</>
              : <>This information is published under the clinical supervision of <strong>{doctorDisplay(CLINIC.doctorName)}, ({CLINIC.qualifications})</strong>, {CLINIC.name}. Please contact the clinic for personalised guidance.</>}
          </footer>
        </div>

        <div className="print-marketing-footer border-t-2 border-pink-600 bg-gradient-to-r from-pink-50 via-white to-pink-50 text-slate-700">
          <div className="grid grid-cols-3 gap-3 px-4 py-2">
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">{isMr ? "पुराव्यावर आधारित" : "Evidence-Based"}</div>
              <div className="text-[10px] mt-0.5 leading-snug">{isMr ? "ADA / ICMR / WHO मार्गदर्शक तत्त्वांवर आधारित रुग्ण-अनुकूल माहिती." : "Guideline-based patient education (ADA / ICMR / WHO)."}</div>
            </div>
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">{isMr ? "नियमित पाठपुरावा" : "Follow-up Matters"}</div>
              <div className="text-[10px] mt-0.5 leading-snug">{isMr ? "नियमित तपासणीसाठी क्लिनिकला भेट द्या. दुर्लक्ष टाळा." : "Book a follow-up review as advised. Do not skip check-ups."}</div>
            </div>
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">{isMr ? "संपर्क" : "Contact"}</div>
              <div className="text-[10px] mt-0.5 leading-snug">{CLINIC.phones?.join(" · ")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
